/**
 * №16.1.4 — non-JSX helpers for the authoring lookups (kept out of FieldPickers.tsx
 * so its fast-refresh boundary stays component-only — the wf-author-shared.ts
 * convention). `useSubjectFields` reads the template's subject entity type — the
 * "declared, queryable fields" registry (D-RS6) the pickers source their options
 * from; `fieldLabel` humanises a field/role identifier.
 */
import { useMemo } from "react";
import { useEntityType } from "../../../lib/hooks/use-kuvio";
import type { FieldDefinition } from "../../../lib/types";

/** snake_/camelCase field name → a human label ("pipeline_stage" → "Pipeline stage"). */
export function fieldLabel(name: string): string {
  const spaced = name.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : name;
}

/**
 * The declared fields of the template's subject entity — the registry the
 * attribute/value/date/status pickers read. `byName` resolves a picked field to
 * its type + choices so the value input types itself; `statusChoices` drives the
 * status picker; `dateFields` the date-field picker.
 */
export function useSubjectFields(subjectType: string | undefined) {
  const et = useEntityType(subjectType);
  return useMemo(() => {
    const fields: FieldDefinition[] = et.data?.fields ?? [];
    const byName = new Map(fields.map((f) => [f.name, f]));
    return {
      fields,
      byName,
      dateFields: fields.filter((f) => f.type === "date" || f.type === "datetime"),
      statusChoices: byName.get("status")?.choices ?? [],
      resolved: !!et.data,
    };
  }, [et.data]);
}
